import { Menu, MenuItem, Popover, Typography } from "@mui/material";
import { Timeline } from "antd";
import { Eye, Package, Printer, RotateCcw, Search, Truck, X, Box } from "lucide-react";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setSubLoading } from "../../Redux/Services/AuthSlice";
import { NotificationSound } from "../CommonComponents/CommonNotification";
import { ApiCall, ConvertToBlobPDF, sanitizeInput, } from "../CommonComponents/CustomHooks";
import { ConvertToBlobImage, virtualPath } from "../CommonComponents/EnvConfig";

/* ─────────────────────────────────────────────
   MOVEMENT HISTORY
───────────────────────────────────────────── */
export const MovementHistory = ({ movementHistoryData = [], height = "auto", handleCloseMenuList, closeIconShow = false }) => {
    if (!movementHistoryData?.length) return null;

    const items = movementHistoryData.map((row) => ({
        label: (
            <div className="text-right pr-2">
                <p className="text-xs font-semibold text-gray-700 m-0 leading-tight">
                    {row?.statusDate?.split(" ")[0]}
                </p>
                <p className="text-[11px] text-gray-400 m-0 leading-tight">
                    {row?.statusDate?.split(" ")[1]}
                </p>

            </div>
        ),
        children: (
            <div className="pb-1">
                <p className="text-xs font-semibold text-gray-800 m-0 leading-tight">{row?.packetStatus}</p>
                {row?.statusDetails && (
                    <p className="text-[11px] text-gray-500 m-0 leading-tight">{row?.statusDetails}</p>
                )}
                {row?.podLocation && (
                    <p className="text-[11px] text-green-600 m-0 leading-tight">{row?.podLocation}</p>
                )}
                <p className="text-xs font-semibold text-gray-700 m-0 leading-tight">
                    {row?.currentBranch?.split(" ")[0]} ({row?.statusUpdateBy?.split(" ")[0]})
                </p>
            </div>
        ),
    }));

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-[#4179a5]">
                <span className="text-xs font-semibold text-white tracking-wide">Tracking History</span>
                {closeIconShow && (
                    <button onClick={handleCloseMenuList} className="text-gray-300 hover:text-white transition-colors">
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            <div style={{ maxHeight: height === "auto" ? "none" : height, overflowY: "auto" }}
                className="bg-white px-3 pt-3 pb-1">
                <Timeline mode="left" items={items} />
            </div>
        </div>
    );
};


/* ─────────────────────────────────────────────
   TRACKING Docket STEPS
───────────────────────────────────────────── */
export const TrackingAWB = ({ TrackingData }) => {
    if (!TrackingData?.stepsArr?.length) return null;
    const isCancelled = TrackingData.stepsArr.some((s) => s.title === "Cancelled");

    return (
        <div className="overflow-x-auto">
            <div className="flex items-start justify-between gap-0 relative min-w-max">
                {/* progress line */}
                <div
                    className="absolute w-[90%] left-[5%] top-5 left-0 h-0.5 z-[2] transition-all"
                    style={{
                        width: TrackingData?.lineWidth || "0%",
                        background: isCancelled
                            ? "linear-gradient(to right,#c30d0d,#ff7b7b)"
                            : "linear-gradient(to right,#16a34a,#22c55e)",
                    }}
                />
                <div className="absolute top-5 w-[90%] left-[5%] right-0 h-0.5 bg-gray-200 z-[1]" />

                {TrackingData.stepsArr.map((item, i) => (
                    <div key={i} className="flex flex-col items-center z-10" style={{ minWidth: 80 }}>
                        <div
                            className={`w-10 h-10 rounded-full border-2 flex items-center justify-center bg-white transition-all
                                ${item.isActive ? "shadow-md shadow-blue-200" : ""}
                            `}
                            style={{
                                borderColor: item.isActive
                                    ? item.title === "Cancelled" ? "#ef4444" : "#3b82f6"
                                    : item.status ? "#16a34a" : "#d1d5db",
                            }}
                        >
                            <img
                                src={item.imageURL}
                                alt={item.title}
                                className="w-5 h-5 object-contain"
                                style={{ opacity: item.status ? 1 : 0.35 }}
                            />
                        </div>
                        <span
                            className={`mt-1 text-center leading-tight px-1
                                ${item.isActive ? "text-blue-600 font-semibold" : item.status ? "text-green-600 font-medium" : "text-gray-400"}
                            `}
                            style={{ fontSize: 10 }}
                        >
                            {item.title}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   CARD COMPONENT
───────────────────────────────────────────── */
export const CardComponent = ({ data = {}, handleClickOnAWBNo, pageName, refreshApi, dataQuery }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const inputrefs = useRef([]);

    const [trackingHistoryData, setTrackingHistoryData] = useState([]);
    const [cardLoadings, setCardLoadings] = useState({ trackingHistoryLoading: false });
    const [anchorElTable, setAnchorElTable] = useState(null);
    const [anchorElMenuList, setAnchorElMenuList] = useState(null);
    const [anchorElActionPopup, setAnchorElActionPopup] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [modalTableData, setModaltableData] = useState({});
    const [modalForm, setModalForm] = useState({ awbNo: "", CancellationReason: "" });
    const [showUpdateNDRModal, setShowUpdateNDRModal] = useState(false);
    const [modalUpdateNDRForm, setModalUpdateNDRForm] = useState({ awbNo: "", ndrRemarks: "" });

    // ── popover helpers ──
    const tableOpen = Boolean(anchorElTable);
    const menuListOpen = Boolean(anchorElMenuList);
    const actionsPopUpOpen = Boolean(anchorElActionPopup);

    const handleCloseMenuList = () => setAnchorElMenuList(null);
    const handleCloseActionPopup = () => setAnchorElActionPopup(null);

    // ── action handlers ──
    const handleTrackAwb = (awbNo) => window.open(`/Report/AWBQuery?AwbNo=${awbNo}`, "_blank");
    const handleAdd = (_, bt) => navigate(`/Operation/Booking/${bt === "Domestic" ? "Domestic" : "International"}`);
    const handleEdit = (awbNo, bt) => navigate(`/Operation/Booking/${bt === "Domestic" ? "Domestic" : "International"}?${new URLSearchParams({ awbNo })}`);
    const handleLabelPrint = (awbNo) => {
        const params = new URLSearchParams({
            awbNo: awbNo,

        });

        const url = `${virtualPath}/LabelPrint?AWBNo=${awbNo}`;

        window.open(url, "_blank");
        handleCloseMenuList();
    };
    const handleCNPrint = (awbNo) => {
        const params = new URLSearchParams({
            awbNo: awbNo,

        });

        const url = `${virtualPath}/CNotePrint?AWBNo=${awbNo}`;

        window.open(url, "_blank");
        handleCloseMenuList();
    };
    const handlePrint = async (path, awbNo) => {
        if (!path) { NotificationSound("ERROR", "Please Give Correct Path", null, null); return; }
        try {
            dispatch(setSubLoading(true));
            const res = await ApiCall(path, { AwbNo: awbNo });
            dispatch(setSubLoading(false));
            if (res.status === "SUCCESS") await ConvertToBlobPDF(res.documentPdfBase64);
            else NotificationSound(res.status, res.message, res.focus, inputrefs);
        } catch { NotificationSound("ERROR", "Failed to show the data.", null, null); }
        finally { dispatch(setSubLoading(false)); }
    };

    const handleViewPOD = async (value) => {
        try {
            dispatch(setSubLoading(true));
            const res = await ApiCall("/api/Report/GetPodImage", { AWBNo: value });
            dispatch(setSubLoading(false));
            if (res.status === "SUCCESS") {

                const byteCharacters = atob(res.podImage);
                const byteNumbers = new Array(byteCharacters.length);

                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);

                const mimeType =
                    res.podExt === "pdf"
                        ? "application/pdf"
                        : `image/${res.podExt}`;

                const blob = new Blob([byteArray], { type: mimeType });
                const url = URL.createObjectURL(blob);

                window.open(url, "_blank");

            }
        } catch { } finally { dispatch(setSubLoading(false)); }
    };

    const handlePullAWB = async (value) => {
        try {
            dispatch(setSubLoading(true));
            const res = await ApiCall("/api/Operation/InsertPullAWBNoStatus", { AWBNo: value });
            dispatch(setSubLoading(false));
            NotificationSound(res.status, res.message, res.focus, inputrefs);
        } catch { } finally { dispatch(setSubLoading(false)); }
    };

    const handleTrackingHistory = async (awbNo, event) => {
        setAnchorElActionPopup(event.currentTarget);
        try {
            setCardLoadings((p) => ({ ...p, trackingHistoryLoading: true }));
            const res = await ApiCall("/api/Report/GetAwbNoMovementDetails", { AwbNo: awbNo, pageName });
            if (res.status === "SUCCESS") setTrackingHistoryData(res.momentDetails);
            else NotificationSound(res.status, res.message, res.focus, inputrefs);
        } catch { NotificationSound("ERROR", "Failed to show the data.", null, null); }
        finally { setCardLoadings((p) => ({ ...p, trackingHistoryLoading: false })); }
    };

    const handleCancelAWB = async (AwbNo) => {
        try {
            dispatch(setSubLoading(true));
            const res = await ApiCall("/api/Report/AwbNoDetailsForCancellation", { AwbNo });
            dispatch(setSubLoading(false));
            if (res.status === "SUCCESS") {
                setShowCancelModal(true);
                setModaltableData(res.data);
                setModalForm((p) => ({ ...p, awbNo: AwbNo }));
            } else NotificationSound(res.status, res.message, res.focus, inputrefs);
        } catch { } finally { dispatch(setSubLoading(false)); }
    };

    const SubmitCancelReason = async () => {
        try {
            dispatch(setSubLoading(true));
            const res = await ApiCall("/api/Report/AwbNoBookingCancellation", { ...modalForm });
            dispatch(setSubLoading(false));
            if (res.status === "SUCCESS") {
                setShowCancelModal(false);
                setModalForm({ awbNo: "", CancellationReason: "" });
                NotificationSound(res.status, res.message, res.focus, inputrefs);
                refreshApi && refreshApi();
            } else NotificationSound(res.status, res.message, res.focus, inputrefs);
        } catch { } finally { dispatch(setSubLoading(false)); }
    };

    const SubmitUpdateNDRReason = async () => {
        try {
            dispatch(setSubLoading(true));
            const res = await ApiCall("/api/Report/PickupNDR", {
                AwbNo: modalUpdateNDRForm.awbNo, Remarks: modalUpdateNDRForm.ndrRemarks,
            });
            dispatch(setSubLoading(false));
            if (res.status === "SUCCESS") {
                setShowUpdateNDRModal(false);
                setModalUpdateNDRForm({ awbNo: "", ndrRemarks: "" });
                NotificationSound(res.status, res.message, res.focus, inputrefs);
                refreshApi && refreshApi();
            } else NotificationSound(res.status, res.message, res.focus, inputrefs);
        } catch { } finally { dispatch(setSubLoading(false)); }
    };

    const actionHandlers = {
        "New Booking": (v) => handleAdd(v, data?.basicDetails?.bookingType),
        "Edit": (v) => handleEdit(v, data?.basicDetails?.bookingType),
        "Track Docket": async (v) => { await handleTrackAwb(v); handleCloseMenuList(); },
        "Cancel": (v) => handleCancelAWB(v),
        "Forwarder Print": async (v) => { await handlePrint("/api/Report/ForwardingLabelPrint", v); handleCloseMenuList(); },
        //"Label Print": async (v) => { await handlePrint("/api/Print/AwbLabelPrint", v); handleCloseMenuList(); },
        "Label Print": async (v) => {
            handleLabelPrint(v);
        },
        "CN Print": async (v) => {
            handleCNPrint(v);
        },
        "Boxwise Invoice Print": async (v) => { await handlePrint("/api/Print/AwbBoxWiseLabelPrint", v); handleCloseMenuList(); },
        "Invoice Print": async (v) => { await handlePrint("/api/Print/AwbInvoicePrint", v); handleCloseMenuList(); },
        "Docket Print": async (v) => { await handlePrint("/api/Print/AwbPrint", v); handleCloseMenuList(); },
        "Tracking History": async (v, e) => { await handleTrackingHistory(v, e); handleCloseMenuList(); },
        "View POD": async (v) => { await handleViewPOD(v); handleCloseMenuList(); },
        "Pull Docket": async (v) => { await handlePullAWB(v); handleCloseMenuList(); },
    };

    // colour helper
    const statusColor = (s = "") => {
        const l = s.toLowerCase();
        if (l === "delivered" || l === "booked") return "bg-green-100 text-green-700 border-green-200";
        if (l === "cancelled") return "bg-red-100 text-red-600 border-red-200";
        return "bg-blue-100 text-blue-700 border-blue-200";
    };

    if (!data) return null;

    return (
        <>
            {/* ── Main Card ── */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-3">

                {/* ── Top Bar: Docket + booking type + status ── */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div style={{ lineHeight: '1' }}>
                            <span className="text-[11px] text-gray-400 font-medium">Docket No. </span>
                            <button
                                className="text-sm font-bold text-green-600 hover:underline cursor-pointer font-mono tracking-wide"
                                onClick={() => handleClickOnAWBNo && handleClickOnAWBNo(data?.basicDetails?.awbNo)}
                                onDoubleClick={() => navigator.clipboard.writeText(data?.basicDetails?.awbNo || "")}
                                title="Double-click to copy"
                            >
                                {data?.basicDetails?.awbNo}
                            </button>
                            <div className="text-[9px] font-semibold text-gray-700 truncate">{data?.basicDetails?.bookingDate}</div>
                        </div>
                        {data?.basicDetails?.bookingType && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-600 text-white font-medium">
                                {data?.basicDetails?.bookingType}
                            </span>
                        )}
                        {data?.basicDetails?.isRTO && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-medium">
                                RTO
                            </span>
                        )}
                    </div>
                    <div className="flex items-baseline gap-2">
                        <i className={`fa fa-route text-gray-500 text-[12px] w-2`} />
                        <span className="text-[12px] font-bold text-gray-700 truncate">{data?.basicDetails?.route}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {data?.actions && (
                            <div className="flex flex-wrap gap-2">
                                {data.actions.map((action, idx) =>
                                    action?.value ? (
                                        action?.key === "View POD" ? (
                                            <></>
                                        ) : (
                                            <button
                                                key={idx}
                                                title={action.key}
                                                onClick={(e) => actionHandlers[action.key](action.value, e)}
                                                className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                                            >
                                                <i className={`fa ${action.icon} text-[11px]`} />
                                            </button>
                                        )
                                    ) : null
                                )}

                                {/* Print menu */}
                                {data?.menuLists?.length > 0 && (
                                    <button
                                        title="Print options"
                                        onClick={(e) => setAnchorElMenuList(e.currentTarget)}
                                        className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                                    >
                                        <Printer className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Body: 3 columns ── */}
                <div className="grid grid-cols-1 md:grid-cols-7 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-100">

                    {/* Col 1: Basic Details */}
                    {data?.basicDetails && (
                        <div className="px-4 py-3 col-span-2">
                            <div className="flex items-center gap-1 mb-2">
                                <i className={`fa-solid ${data?.basicDetails?.serviceIcon}`}></i>
                                <span className="text-sm font-bold text-gray-800 truncate">{data?.basicDetails?.companyName}</span>
                                {data?.basicDetails?.bookingStatus && (
                                    <span className={`text-[10px] px-2.5 py-0.5 rounded-full border font-semibold ${statusColor(data?.basicDetails?.bookingStatus)}`}>
                                        {data?.basicDetails?.bookingStatus}
                                    </span>
                                )}
                                {data?.actions?.find(item => item.key === "View POD") && data?.basicDetails?.bookingStatus?.toUpperCase() === 'DELIVERED' && (
                                    <span title='View POD'>
                                        <Printer
                                            className='w-4 h-4 text-[#3C768E] cursor-pointer'
                                            onClick={(e) => actionHandlers["View POD"](data?.basicDetails?.awbNo, e)}
                                        />
                                    </span>
                                )}
                            </div>
                            <div className="space-y-0.5">
                                {[
                                    { icon: "fa-cogs", label: "Ref No", val: data?.basicDetails?.refNo },
                                    { icon: "fa-user", label: "Client", val: data?.basicDetails?.client },
                                    { icon: "fa-shipping-fast", label: "Forwarding No", val: data?.basicDetails?.forwardingNo },
                                    { icon: "fa-user", label: "Vendor", val: data?.basicDetails?.vendor },
                                    { icon: "fa-calendar", label: "Delivered", val: data?.basicDetails?.deliveredDate },
                                    { icon: "fa-calendar", label: "Pickup Date", val: data?.basicDetails?.pickupDate },
                                    { icon: "fa-shipping-fast", label: "Booking Branch", val: data?.basicDetails?.originBranch },
                                    { icon: "fa-shipping-fast", label: "Destination Branch", val: data?.basicDetails?.destinationBranch },
                                    { icon: "fa-credit-card", label: "Payment Type", val: data?.basicDetails?.paymentType },
                                    { icon: "fa-route", label: "Shipment Type", val: data?.basicDetails?.shipmentType },
                                    { icon: "fa-cog", label: "Service Name", val: data?.basicDetails?.serviceName },
                                ].filter(r => r.val).map((row, i) => (
                                    <div key={i} className="flex items-baseline gap-2">
                                        <i className={`fa ${row.icon} text-gray-300 text-[11px] w-2`} />
                                        <span className="text-[10px] text-gray-400 shrink-0">{row.label}:</span>
                                        <span className="text-[10px] font-semibold text-gray-700 truncate">{row.val}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="px-4 py-3 col-span-3">
                        {dataQuery?.momentStepDetails && (
                            <TrackingAWB
                                TrackingData={dataQuery.momentStepDetails}
                            />
                        )}
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            {data?.consignordetails && (
                                <div>
                                    <p className="text-[10px] font-bold text-red-500 uppercase tracking-wide mb-1">Consignor</p>
                                    <div className="space-y-0.5">
                                        {data.consignordetails.filter(i => i.value).map((item, i) => (
                                            <div key={i} className="flex items-baseline gap-1">
                                                <i className={`${item.icon} text-[10px] text-gray-300 w-3`} />
                                                <span className="text-[10px] font-semibold text-gray-700 leading-tight">{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {data?.consigneedetails && (
                                <div>
                                    <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wide mb-1">Consignee</p>
                                    <div className="space-y-0.5">
                                        {data.consigneedetails.filter(i => i.value).map((item, i) => (
                                            <div key={i} className="flex items-baseline gap-1">
                                                <i className={`${item.icon} text-[10px] text-gray-300 w-3`} />
                                                <span className="text-[10px] font-semibold text-gray-700 leading-tight">{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {(data?.packageDetails || data?.boxDimensions) && (
                        <div className="px-4 py-3 col-span-2">
                            {data?.packageDetails && (
                                <div>
                                    <p className="flex items-center gap-2 text-[10px] font-bold text-amber-600 uppercase tracking-wide mb-1.5">Package Details (Pcs:{data?.basicDetails?.totalPcs || 0})
                                        {data?.boxDimensions && (
                                            <div>
                                                <button
                                                    onClick={(e) => setAnchorElTable(e.currentTarget)}
                                                    className="flex items-center gap-1 text-[11px] text-red-500 font-semibold hover:underline"
                                                >
                                                    <Box className="w-5 h-5" />
                                                </button>
                                                <Popover
                                                    open={tableOpen}
                                                    anchorEl={anchorElTable}
                                                    onClose={() => setAnchorElTable(null)}
                                                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                                                >
                                                    <Typography sx={{ p: "6px", fontSize: "12px", bgcolor: "#ffcccc" }}>
                                                        <table className="table table-bordered mb-0">
                                                            <thead>
                                                                <tr>
                                                                    {data.boxDimensions[0]?.boxDimensionItems?.map((e, i) => (
                                                                        <th key={i}>{e.key}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {data.boxDimensions.map((dim, ri) => (
                                                                    <tr key={ri}>
                                                                        {data.boxDimensions[0]?.boxDimensionItems?.map((h, hi) => {
                                                                            const cell = dim.boxDimensionItems?.find(c => c.key === h.key);
                                                                            return <td key={hi}>{cell?.value || ""}</td>;
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </Typography>
                                                </Popover>
                                            </div>
                                        )}
                                    </p>
                                    <div className="flex gap-2">
                                        {data.packageDetails.map((item, i) => (
                                            <div key={i} className="card text-center p-2 mb-0 border border-1">
                                                <p className="text-[10px] text-gray-400 shrink-0">{item.key}:</p>
                                                <p className="text-[10px] font-semibold text-gray-700">{item.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                    {data?.basicDetails?.packetDescription &&
                                        <div className="mt-2" style={{ lineHeight: '1' }}>
                                            <p className="text-[10px] text-gray-400 shrink-0">Packet Description </p>
                                            <span className="text-[10px] font-semibold text-gray-700 leading-tight">{data?.basicDetails?.packetDescription || ""}</span>
                                        </div>
                                    }
                                </div>
                            )}
                            {/* NDR Update */}
                            {data?.basicDetails?.isUpdateNDR && (
                                <button
                                    className="mt-2 px-3 py-1 text-[11px] font-medium rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                                    onClick={() => { setModalUpdateNDRForm(p => ({ ...p, awbNo: data?.basicDetails?.awbNo })); setShowUpdateNDRModal(true); }}
                                >
                                    NDR Update
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Print menu popup ── */}
            {data?.menuLists?.length > 0 && (
                <Menu
                    anchorEl={anchorElMenuList}
                    open={menuListOpen}
                    onClose={handleCloseMenuList}
                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{ paper: { elevation: 0, sx: { overflow: "visible", filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.18))", mt: 0.5, bgcolor: "var(--bg-light)", "& .MuiList-root": { my: 0, py: 0 } } } }}
                >
                    {data.menuLists.map((ele, i) => (
                        <MenuItem key={i} style={{ width: 160 }}
                            className="text-[12px] flex justify-between gap-2"
                            onClick={() => actionHandlers[ele.key](ele.value)}
                        >
                            <span className="text-[12px] truncate">{ele.key}</span>
                            <i className={`fa ${ele.icon}`} />
                        </MenuItem>
                    ))}
                </Menu>
            )}

            {/* ── Tracking History popup ── */}
            {trackingHistoryData?.length > 0 && (
                <Menu
                    open={actionsPopUpOpen}
                    anchorEl={anchorElActionPopup}
                    onClose={handleCloseActionPopup}
                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{ paper: { elevation: 0, sx: { overflow: "visible", filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.18))", mt: 0.5, width: "40vw", bgcolor: "var(--bg-light)", "& .MuiList-root": { my: 0, py: 0 } } } }}
                >
                    <MovementHistory
                        movementHistoryData={trackingHistoryData}
                        handleCloseMenuList={handleCloseActionPopup}
                        closeIconShow={true}
                    />
                </Menu>
            )}

            {/* ── Cancel Modal ── */}
            {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-800">Cancel Docket</h3>
                            <button onClick={() => { setShowCancelModal(false); setModalForm({ awbNo: "", CancellationReason: "" }); }}>
                                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </button>
                        </div>
                        <div className="p-4">
                            {modalTableData?.length > 0 && (
                                <div className="overflow-auto mb-3 max-h-40 rounded border border-gray-200">
                                    <table className="table table-bordered mb-0 text-xs">
                                        <thead className="bg-gray-50">
                                            <tr>{Object.keys(modalTableData[0]).map((k, i) => <th key={i}>{k}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {modalTableData.map((row, i) => (
                                                <tr key={i}>{Object.values(row).map((v, j) => <td key={j}>{v}</td>)}</tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Cancellation Reason</label>
                            <textarea
                                rows={3}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                value={modalForm.CancellationReason}
                                onChange={(e) => setModalForm(p => ({ ...p, CancellationReason: e.target.value }))}
                                placeholder="Enter reason..."
                            />
                            <div className="flex gap-2 justify-end mt-3">
                                <button onClick={() => { setShowCancelModal(false); setModalForm({ awbNo: "", CancellationReason: "" }); }}
                                    className="px-4 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                                    Discard
                                </button>
                                <button onClick={SubmitCancelReason}
                                    className="px-4 py-1.5 text-xs rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition-colors">
                                    Confirm Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── NDR Modal ── */}
            {showUpdateNDRModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-800">Update NDR</h3>
                            <button onClick={() => { setShowUpdateNDRModal(false); setModalUpdateNDRForm({ awbNo: "", ndrRemarks: "" }); }}>
                                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </button>
                        </div>
                        <div className="p-4">
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">NDR Remarks</label>
                            <textarea
                                rows={3}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                value={modalUpdateNDRForm.ndrRemarks}
                                onChange={(e) => setModalUpdateNDRForm(p => ({ ...p, ndrRemarks: e.target.value }))}
                                placeholder="Enter NDR remarks..."
                            />
                            <div className="flex gap-2 justify-end mt-3">
                                <button onClick={() => { setShowUpdateNDRModal(false); setModalUpdateNDRForm({ awbNo: "", ndrRemarks: "" }); }}
                                    className="px-4 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button onClick={SubmitUpdateNDRReason}
                                    className="px-4 py-1.5 text-xs rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors">
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

/* ─────────────────────────────────────────────
   Docket No QUERY  (main page)
───────────────────────────────────────────── */
const AWBNoQuery = () => {
    const inputrefs = useRef([]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ awbNo: "" });
    const [dataQuery, setdataQuery] = useState({});
    const [searched, setSearched] = useState(false);

    useEffect(() => { document.title = "Docket No Query"; }, []);

    const handleClear = () => {
        setFormData({ awbNo: "" });
        setdataQuery({});
        setSearched(false);
        navigate('/Query/AwbNoQuery')

    };
    const handlequeryData = async () => {
        handlequeryDataWithValue(formData.awbNo);
    };
    const handlequeryDataWithValue = async (awbValue) => {
        await dispatch(setSubLoading(true));
        const res = await ApiCall("/api/Query/GetAwbNoQueryDetails", { AwbNo: awbValue || "" });
        await dispatch(setSubLoading(false));
        if (["UNAUTHORIZED", "ERROR", "EXPIRED"].includes(res.status)) {
            await NotificationSound(res.status, res.message, res.focus, inputrefs);
        } else {
            setdataQuery(res);
            setSearched(true);
        }
    };

    const hasResults = dataQuery?.data?.length > 0 || dataQuery?.momentStepDetails || dataQuery?.momentDetails;
    useEffect(() => {
        document.title = "Docket No Query";
        const params = new URLSearchParams(window.location.search);
        const searchingNo = params.get("SearchingNo");
        if (searchingNo) {
            const cleanValue = sanitizeInput(searchingNo);
            setFormData({ awbNo: cleanValue });
            handlequeryDataWithValue(cleanValue);
        }
    }, []);
    return (
        <div className="min-h-screen space-y-3">

            {/* ── Search Card ── */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3">
                <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-gray-100">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Package className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                        <h1 className="text-sm font-semibold text-gray-800 leading-tight">Docket No. Query</h1>
                        <p className="text-[11px] text-gray-400 leading-tight">Track shipment by Docket number</p>
                    </div>
                </div>

                <div className="flex gap-2 items-end">
                    <div className="flex-2">
                        <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">
                            Docket Number
                        </label>
                        <input
                            ref={(ref) => (inputrefs.current["txtAwbNo"] = ref)}
                            id="txtAwbNo"
                            type="text"
                            maxLength={30}
                            value={formData.awbNo}
                            placeholder="Enter Docket No"
                            onChange={(e) => setFormData({ ...formData, awbNo: sanitizeInput(e.target.value) })}
                            onKeyDown={(e) => { if (e.key === "Enter") handlequeryData(); }}
                            className="w-full h-9 px-3 rounded-lg border border-gray-200 bg-gray-50 text-sm font-mono tracking-wide text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handlequeryData}
                        ref={(ref) => (inputrefs.current["txtSearch"] = ref)}
                        className="h-9 px-4 rounded-lg bg-[#4179a5] hover:bg-blue-700 active:scale-95 text-white text-xs font-medium flex items-center gap-1.5 transition-all flex-shrink-0"
                    >
                        <Search className="w-3.5 h-3.5" /> Search
                    </button>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="h-9 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 active:scale-95 text-gray-500 text-xs font-medium flex items-center gap-1.5 transition-all flex-shrink-0"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Clear
                    </button>
                </div>
            </div>

            {/* ── Results Card ── */}
            {searched && hasResults && (
                <div className="overflow-hidden">

                    {/* Shipment cards */}
                    {dataQuery?.data?.length > 0 && (
                        <div className="p-0">
                            {dataQuery.data.map((item, index) => (
                                <React.Fragment key={index}>
                                    <CardComponent data={item} pageName="AWBQuery" dataQuery={dataQuery} />
                                </React.Fragment>
                            ))}
                        </div>
                    )}

                    {/* Movement history */}
                    {dataQuery?.momentDetails && (
                        <div className="px-2 border rounded-lg shadow-sm py-2 border-t border-gray-100">
                            <MovementHistory movementHistoryData={dataQuery.momentDetails} height="100%" />
                        </div>
                    )}
                </div>
            )}

            {/* ── Empty state ── */}
            {searched && !hasResults && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <Truck className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-600">No records found</p>
                    <p className="text-xs text-gray-400">Check the Docket number and try again</p>
                </div>
            )}
        </div>
    );
};

export default AWBNoQuery;